import * as React from 'react';

import { ILink, ILinkTableState } from 'src/views/Links/types';

import { Button, Icons, Tooltip } from 'src/components';
import { TableCell } from 'src/views/Links/components/TableCell';

import API from 'src/services/api';
import { getParameterNames } from 'src/services/links/getParameterNames';
import { calculateIDWidth } from 'src/services/links/calculateIDWidth';
import { calculateTableWidth } from 'src/services/links/calculateTableWidth';

interface IDrawerProps {
  item: any;
  open: boolean;
  close: () => void;
  startBulkWithCopy: (market: string, template: string, links: any[]) => void;
  profileParameterName: string;
  parameterOrder: Array<{
    name: string;
    order: number;
  }>;
  copying: boolean;
}
interface IDrawerState extends ILinkTableState {
  links: ILink[];
  loadingLinks: boolean;
  checkedRows: number[];
}

class Drawer extends React.Component<IDrawerProps, IDrawerState> {
  state: IDrawerState = {
    links: [],
    loadingLinks: false,
    parameterNames: [],
    checkedRows: [],
    tableWidth: 0,
    IDWidth: 0,
  };

  componentDidMount() {
    this.fetchNewLinks();
  }
  componentDidUpdate(prevProps: IDrawerProps) {
    if (prevProps.item.id !== this.props.item.id && this.props.open) {
      this.fetchNewLinks();
    }
  }

  fetchNewLinks = async () => {
    this.setState({
      loadingLinks: true,
    });
    await this.fetchLinks();
    this.setState({
      loadingLinks: false,
    });
  };

  fetchLinks = async () => {
    const { getData } = API.LINK_UPLOAD.getBulkUpload(
      this.props.item.id,
      { page: 1, linksPerPage: 10000 },
      this.props.item.profile.name,
      this.props.profileParameterName
    );
    const { links } = await getData();
    this.setState(
      {
        links,
      },
      this.updateColumnWidths
    );
  };

  startBulkWithCopies = () => {
    const { item } = this.props;
    const { checkedRows, links } = this.state;
    const checkedLinks = links
      .filter((_, index) => checkedRows.includes(index))
      .map((link) => link.parameters);
    this.props.startBulkWithCopy(
      item.profile.id,
      item.template.id,
      checkedLinks
    );
  };

  updateColumnWidths() {
    const { parameterOrder, profileParameterName } = this.props;
    const parameterNames = getParameterNames(
      this.state.links,
      parameterOrder,
      profileParameterName
    );
    const IDWidth = calculateIDWidth(this.state.links);
    this.setState({
      parameterNames,
      IDWidth,
      tableWidth: calculateTableWidth(parameterNames, 10 + IDWidth),
    });
  }

  getParametersByName(link: ILink) {
    const { parameterNames } = this.state;
    return parameterNames.map(({ name, length }) => {
      const possibleParameter = link.parameters.find((p) => p.name === name);
      if (!possibleParameter) {
        return { value: '', length };
      }
      return { value: possibleParameter.value, length };
    });
  }

  toggleCheckedRow = (row: number) => () => {
    this.setState((state) => ({
      checkedRows: state.checkedRows.includes(row)
        ? state.checkedRows.filter((r) => r !== row)
        : [...state.checkedRows, row],
    }));
  };
  toggleAllCheckedRows = () => {
    this.setState((state) => ({
      checkedRows:
        state.checkedRows.length === state.links.length
          ? []
          : state.links.map((_, i) => i),
    }));
  };

  render() {
    const {
      links,
      loadingLinks,
      parameterNames,
      tableWidth,
      IDWidth,
      checkedRows,
    } = this.state;
    const { open, item, copying, profileParameterName } = this.props;
    const openClasses = open ? '' : 'translate-top-full';
    const anyRowChecked = copying && checkedRows.length > 0;
    const allRowsChecked = copying && checkedRows.length === links.length;

    return (
      <div
        className={`fixed z-50 pin-b pin-l pin-r bg-white shadow-lg pt-6 pb-8 px-8 transition-transform ${openClasses}`}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-xl font-bold mb-2">
              {copying ? 'Copying' : 'Viewing'} Links from{' '}
              <span className="font-mono text-base mx-2">{item.name}</span>
            </p>
            <p>
              <span className="opacity-75 italic">Created on:</span>{' '}
              {item.createdOn}{' '}
              <span className="opacity-75 italic">{profileParameterName}:</span>{' '}
              {item.profile.name}
            </p>
          </div>
          <button
            onClick={this.props.close}
            className="px-2 py-1 hover:text-red"
          >
            <span className="align-middle text-xl">&times;</span> Close
          </button>
        </div>

        {loadingLinks ? (
          <div className="loader opacity-50 h-48 mb-4" />
        ) : (
          <>
            <div className="max-w-full max-h-drawer overflow-auto">
              <div style={{ width: `${tableWidth}em` }}>
                <div className="flex sticky pin-t z-20 bg-white">
                  <Tooltip
                    text="Select all rows"
                    position="bottom"
                    side="left"
                    disabled={!copying}
                  >
                    <label
                      className={`relative flex items-center justify-end w-8 h-full px-2 ${
                        allRowsChecked ? 'bg-yellow' : 'bg-grey-dark'
                      } text-sm font-mono cursor-pointer`}
                    >
                      <input
                        onChange={this.toggleAllCheckedRows}
                        type="checkbox"
                        className="absolute opacity-0 pointer-events-none"
                        disabled={!copying}
                      />
                      {copying && <Icons.Checkbox />}
                    </label>
                  </Tooltip>
                  <div
                    className={`p-2 border-r border-white ${
                      copying ? 'bg-yellow' : 'bg-blue text-white'
                    }`}
                    style={{ width: '10rem' }}
                  >
                    Created on
                  </div>
                  <div
                    className={`p-2 border-r border-white ${
                      copying ? 'bg-yellow' : 'bg-blue text-white'
                    }`}
                    style={{ width: `${IDWidth}em` }}
                  >
                    ID
                  </div>
                  {parameterNames.map(({ name, length }) => (
                    <div
                      key={name}
                      className={`p-2 border-r border-white ${
                        copying ? 'bg-yellow' : 'bg-blue text-white'
                      }`}
                      style={{ width: `${length}em` }}
                    >
                      {name}
                    </div>
                  ))}
                </div>
                {links.map((link, index) => {
                  const parametersByName = this.getParametersByName(link);
                  const isChecked = copying && checkedRows.includes(index);
                  return (
                    <div
                      key={link.id}
                      className={`border-b border-grey-light ${
                        isChecked ? 'bg-yellow-light' : 'bg-grey'
                      }`}
                    >
                      <div className="flex w-full text-black text-left hover:bg-grey-light">
                        <label
                          className={`sticky pin-l z-10 relative flex items-center justify-end w-8 px-2 ${
                            isChecked ? 'bg-yellow' : 'bg-grey-dark'
                          } text-sm font-mono cursor-pointer`}
                        >
                          <input
                            onChange={this.toggleCheckedRow(index)}
                            type="checkbox"
                            className="absolute opacity-0 pointer-events-none"
                            disabled={!copying}
                          />
                          {index + 1}
                        </label>
                        <TableCell width={10}>{link.created}</TableCell>
                        <TableCell width={IDWidth} className="font-mono">
                          {link.visibleId}
                        </TableCell>
                        {parametersByName.map(({ value, length }, i) => (
                          <TableCell key={value + i} width={length}>
                            {value}
                          </TableCell>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {copying && (
              <div className="relative flex justify-center py-4">
                <p className="absolute pin-t pin-l text-sm px-2 py-1 text-grey-darker">
                  <span className="inline-block align-middle mr-2 mb-1">↑</span>{' '}
                  Select rows here
                </p>
                <Button
                  onClick={this.startBulkWithCopies}
                  disabled={!anyRowChecked}
                  className="block"
                >
                  Start new bulk upload with {checkedRows.length} selected links
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}
export default Drawer;



// WEBPACK FOOTER //
// ./src/views/LinkCreateBulkStart/components/Drawer.tsx